/**
 * API client for user settings management
 */

import type {
  ModuleName,
  SettingsSection,
  UserSettings,
  UserSettingsData,
} from "@/types/user-settings"

import { api } from "@/lib/api-client"

/**
 * Fetch all user settings
 */
export async function fetchUserSettings(): Promise<UserSettings> {
  return api.get<never, UserSettings>("/user-settings")
}

/**
 * Update all user settings
 */
export async function updateUserSettings(data: Partial<UserSettingsData>): Promise<UserSettings> {
  return api.put<never, UserSettings>("/user-settings", { data })
}

/**
 * Update a specific settings section
 */
export async function updateSettingsSection(
  section: SettingsSection,
  data: Record<string, unknown>
): Promise<UserSettings> {
  return api.patch<never, UserSettings>(`/user-settings/${section}`, data)
}

/**
 * Toggle favorite status for a session
 */
export async function toggleFavoriteSession(
  module: ModuleName,
  sessionId: string
): Promise<{
  session_id: string
  module: string
  is_favorite: boolean
  settings: UserSettings
}> {
  return api.post<
    never,
    {
      session_id: string
      module: string
      is_favorite: boolean
      settings: UserSettings
    }
  >(`/user-settings/favorite-sessions/${module}/${sessionId}`)
}

/**
 * Toggle favorite status for a document
 */
export async function toggleFavoriteDocument(documentId: string): Promise<{
  document_id: string
  is_favorite: boolean
  settings: UserSettings
}> {
  return api.post<
    never,
    {
      document_id: string
      is_favorite: boolean
      settings: UserSettings
    }
  >(`/user-settings/favorite-documents/${documentId}`)
}
